import React from 'react'
import { Check } from 'lucide-react'
import classNames from 'classnames'
import { UserFile } from '../../../../../service/user'
import { useImageRatio } from '../hooks/useImageRatio'

interface ImageCardProps {
  file?: UserFile
  src?: string
  alt?: string
  size?: 'small' | 'medium' | 'large' | 'mobile'
  className?: string
  isSelected?: boolean
  onClick?: (file?: UserFile) => void | undefined
}

const sizeClasses = {
  small: 'w-[136px] h-[136px]',
  medium: 'w-[138px] h-[138px]',
  large: 'w-[200px] h-[200px]',
  mobile: 'w-[176px] h-[176px]',
}

export const ImageCard: React.FC<ImageCardProps> = ({
  file,
  src,
  alt,
  size = 'medium',
  className,
  isSelected = false,
  onClick,
}) => {
  const imageUrl = src || file?.fileUrl || ''
  const imageAlt = alt || getDefaultAltText(file)

  const {
    imageRatio,
    isError,
    handleImageLoad,
    handleImageError,
    getImageClasses,
    getBackgroundStyle,
  } = useImageRatio(imageUrl)

  const handleClick = () => {
    onClick?.(file)
  }

  function getDefaultAltText(file?: UserFile): string {
    if (!file) return '이미지'

    if (file.fileType === 'direct_upload') {
      return `업로드 파일 ${file.id}`
    }
    return `생성 이미지 ${file.id}`
  }

  const containerClasses = classNames(
    sizeClasses[size],
    'rounded-[10px] overflow-hidden cursor-pointer transition-all relative',
    {
      'ring-4 ring-[#00B7B9]': isSelected,
    },
    className
  )

  if (isError) {
    return (
      <div
        onClick={handleClick}
        className={`${containerClasses} flex items-center justify-center bg-gray-200`}
      >
        <span className="text-gray-500 text-sm">이미지 로드 실패</span>
      </div>
    )
  }

  return (
    <div
      onClick={handleClick}
      className={containerClasses}
      style={getBackgroundStyle(imageUrl)}
    >
      {/* 블러된 배경 오버레이 (세로/가로 이미지용) */}
      {(imageRatio === 'portrait' || imageRatio === 'landscape') && (
        <div className="  bg-black" />
      )}

      {/* 실제 이미지 */}
      <img
        src={imageUrl}
        alt={imageAlt}
        className={`${getImageClasses()} relative z-10`}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />

      {/* 선택 체크마크 */}
      {isSelected && (
        <div className="absolute bottom-2 right-2 w-7 h-7 bg-[#00B7B9] rounded-full border-2 border-white flex items-center justify-center z-20">
          <Check className="w-4 h-4 text-white" />
        </div>
      )}

      {/* 로딩 상태 표시 */}
      {imageRatio === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-[#00B7B9] rounded-full animate-spin" />
        </div>
      )}
    </div>
  )
}
